import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Badge,
  Box,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
} from '@mui/material';
import { ProChip } from '@/components/common/ProChip';
import { RouteConfig } from '@/routes';
import { sidebar } from '@/styles/tokens';
import { ModeConfig, SidebarMode } from './sidebar.types';

interface SidebarMenuItemProps {
  item: RouteConfig;
  isCollapsed: boolean;
  isMobile: boolean;
  currentMode: SidebarMode;
  activeConfig: ModeConfig;
  badgeContent: number;
  isSelected: boolean;
  onClick: () => void;
}

export const SidebarMenuItem: React.FC<SidebarMenuItemProps> = ({
  item,
  isCollapsed,
  isMobile,
  currentMode,
  activeConfig,
  badgeContent,
  isSelected,
  onClick,
}) => {
  const { t } = useTranslation();
  const showText = !isCollapsed || isMobile;
  const isDark = !['general', 'client', 'expert'].includes(currentMode);
  const isLight = !isDark;

  const tooltipBg = isDark ? activeConfig.bgColor : sidebar.tooltipBgLight;
  const itemName = item.name ? t(item.name) : '';
  const tooltipLabel = badgeContent > 0 ? `${itemName} (${badgeContent})` : itemName;

  return (
    <ListItem disablePadding sx={{ display: 'block' }}>
      <Tooltip
        title={isCollapsed && !isMobile ? tooltipLabel : ''}
        placement="right"
        arrow
        enterDelay={0}
        enterNextDelay={0}
        slotProps={{
          tooltip: {
            sx: {
              bgcolor: tooltipBg,
              color: '#fff',
              fontSize: '0.8125rem',
              fontWeight: 500,
              letterSpacing: '0.01em',
              px: 1.5,
              py: 0.75,
              borderRadius: 1.5,
              boxShadow: isDark ? '0 4px 16px rgba(0,0,0,0.5)' : '0 4px 12px rgba(0,0,0,0.25)',
              border: '1px solid',
              borderColor: isDark ? 'rgba(255,255,255,0.15)' : 'rgba(255,255,255,0.08)',
            },
          },
          arrow: { sx: { color: tooltipBg } },
        }}
      >
        <ListItemButton
          onClick={onClick}
          selected={isSelected}
          sx={{
            minHeight: 40,
            justifyContent: isCollapsed && !isMobile ? 'center' : 'initial',
            px: 2,
            '&.Mui-selected': {
              backgroundColor: !isLight ? sidebar.darkSelected : undefined,
              '&:hover': { backgroundColor: !isLight ? sidebar.darkSelectedHover : undefined },
            },
            '&:hover': { backgroundColor: !isLight ? sidebar.darkHover : undefined },
          }}
        >
          <ListItemIcon
            sx={{
              minWidth: 0,
              mr: showText ? 1.5 : 'auto',
              justifyContent: 'center',
              color: !isLight ? 'inherit' : undefined,
            }}
          >
            <Badge color="error" badgeContent={badgeContent} invisible={badgeContent === 0}>
              {item.icon ? React.cloneElement(item.icon as React.ReactElement, { fontSize: 'small' }) : null}
            </Badge>
          </ListItemIcon>
          {showText && (
            <ListItemText
              primary={itemName}
              primaryTypographyProps={{
                fontSize: '0.85rem',
                noWrap: true,
                fontWeight: isSelected ? 600 : 400
              }}
            />
          )}
          {showText && item.requiredPlan === 'pro' && (
            <Box sx={{ ml: 0.5, flexShrink: 0 }}><ProChip /></Box>
          )}
        </ListItemButton>
      </Tooltip>
    </ListItem>
  );
};
